import React, { useState, useEffect, useLayoutEffect, useContext } from 'react'
import { types } from '../types.js'
import { BrManageContentButton } from '@bloomreach/react-sdk'
import { unpackDocuments } from './articleUtilities.js'

import {
  colorMap,
  styleMap,
  getLink,
  getLinkUrl,
  getLinkFromRef,
  LayoutContext,
  NavigationContext,
  getLocalPath,
  getSharedSiteName,
  usePagination,
  removeSpaces,
  updateArray,
  getUrlParameter,
  isScreenMedium,
  isScreenSmall,
  tryParseJSON,
  useTaxonomies,
  getMargins,
} from '../../utility'

import { fetchData } from '../../data'

import { Banner, ArticleDetail, Title, Link2, Filter } from '..'
import { Grid, GridItem } from '../grid/Grid.jsx'

import './articlelist.css'
import { isInstitutional } from '../../../global/authentication/authentication-manager'

export function ArticleList({ page, component }) {
  const searchParamId = getUrlParameter('id')
  const searchTopic = getUrlParameter('topic')
  const navContainerHeight =
    document.getElementsByClassName('nav-container')[0]?.offsetHeight || 0
  const taxonomies = useTaxonomies()
  const [items, setItems] = useState([])
  const [keys, setKeys] = useState([])
  const [selectedKeys, setSelectedKeys] = useState([])
  const [selectedSearch, setSelectedSearch] = useState('')
  const [selectedItem, setSelectedItem] = useState()
  const [filteredItems, setFilteredItems] = useState([])
  const [columnCount, setColumnCount] = useState(null)
  const [groups, setGroups] = useState([])
  const [isSticky, setIsSticky] = useState(false)

  const [isFetching, setIsFetching] = useState()
  const [apiDocument, setApiDocument] = useState()
  const [apiItems, setApiItems] = useState()

  const [parameters, setParameters] = useState({
    layout: null,
    navigation: null,
    alignment: null,
    style: null,
    titleStyle: null,
    backgroundColor: null,
    insetBackground: false,
    filter: null,
    scope: null,
    sortField: null,
    pageSize: null,
    token: null,
    subscription: null,
    gridSize: null,
    derived: {
      isSteps: null,
      isGallery: null,
      isInset: null,
      isLinks: null,
      isAccordion: null,
      isTabs: null,
      isTabWide: null,
      isBanner: null,
      isList: null,
    },
    marginTop: null,
    marginBottom: null,
  })

  const [blurb, setBlurb] = useState({
    id: null,
    document: null,
    title: null,
    subtitle: null,
    time: null,
    publicationDate: null,
    html: null,
    imageUrl: null,
    link: null,
    videoId: null,
    itemType: null,
    itemPrefix: null,
  })

  const [styles, setStyles] = useState({
    backgroundColor: null,
    itemBackgroundColor: null,
  })

  const _parameters = component.getParameters()

  const navigation = useContext(NavigationContext)

  const pageLayout = useContext(LayoutContext)

  const pagination = usePagination({ page, component, parameters: _parameters })

  useEffect(() => {
    resetOnPageLoad()
  }, [page, component])

  useEffect(() => {
    if (
      !(searchParamId && _parameters.scope && _parameters.token) &&
      pagination &&
      pagination.length > 0
    ) {
      setItems(pagination)
    }
  }, [page, component, pagination])

  useEffect(() => {
    const _models = component.getModels()
    const { document: _documentRef } = _models
    const _document = _documentRef && page.getContent(_documentRef)
    const mobileSize = window.matchMedia(
      '(min-width: 48rem) and (max-width: 64rem)'
    ).matches
      ? 2
      : 1
    const columnCountBasedOnScreenSize = window.matchMedia('(min-width: 64rem)')
      .matches
      ? _parameters.linkColumnSize
      : mobileSize
    setParameters({
      layout: _parameters.layout,
      navigation: _parameters.navigation,
      alignment: _parameters.alignment,
      style: _parameters.style,
      titleStyle: _parameters.titleStyle,
      backgroundColor: _parameters.backgroundColor,
      insetBackground: _parameters.insetBackground,
      filter: _parameters.filter,
      scope: _parameters.scope,
      sortField: _parameters.sortField,
      pageSize: _parameters.pageSize,
      token: _parameters.token,
      subscription: _parameters.subscription,
      forceH2: _parameters.forceH2,
      gridSize: _parameters.linkColumnSize || 0,
      derived: {
        isList: true,
        columnCountBasedOnScreenSize: columnCountBasedOnScreenSize,
        isSteps: _parameters.layout?.indexOf('Steps') > -1,
        isGallery: _parameters.layout?.indexOf('Gallery') > -1,
        isInset: _parameters.layout?.indexOf('Inset') > -1,
        isLinks: _parameters.layout?.indexOf('Links') > -1,
        isAccordion: _parameters.layout?.indexOf('Accordion') > -1,
        isTabs: _parameters.layout?.indexOf('Tabs') > -1,
        isTabsWide: _parameters.layout?.indexOf('WideTabs') > -1,
        isBanner: _parameters.layout?.indexOf('Banner') > -1,
        isNarrow: _parameters.layout?.indexOf('Narrow') > -1,
        isCard:
          _parameters.style?.indexOf('Card') > -1 ||
          _parameters.style?.indexOf('Gallery') > -1, // || _parameters.layout?.indexOf('Steps') > -1
      },
      marginTop: _parameters.marginTop || 'Large',
      marginBottom: _parameters.marginBottom || 'Large',
    })

    setStyles({
      ...colorMap[_parameters.backgroundColor || 'None'],
      ...styleMap[_parameters.style || 'None'],
    })

    const translatedData = {}

    if (_document) {
      const data = _document?.getData() || {}
      translatedData.id = data.id
      translatedData.title = data.title
      translatedData.subtitle = data.subtitle || data.subhead
      translatedData.publicationDate = data.publicationDate
      translatedData.html =
        data.html && typeof data.html === 'object'
          ? data.html.value
          : data.html || data.content
      translatedData.imageRef = data.image || data.heroImage
      translatedData.link = data.linkCompound
    }

    const { id, title, subtitle, publicationDate, html, link } = translatedData

    setBlurb({
      id: id,
      document: _document,
      title: title,
      subtitle: subtitle,
      publicationDate: publicationDate,
      html: html,
      imageUrl: null,
      link: getLink(getLinkFromRef(link, page), null),
    })
  }, [page, component, component?.model, _parameters])

  useEffect(() => {
    if (!searchParamId) {
      setApiDocument(null)
      setApiItems(null)
    }
    /*
      DEV NOTE: Needs to reference `_parameters` not `parameters` since the latter is null until the above
      useEffect runs, and we need to know the info upon first run of this useEffect
      */
    if (searchParamId && _parameters.scope && _parameters.token) {
      /*
        Then only do this logic once state is populated
        */
      if (
        parameters.scope &&
        parameters.token &&
        (!apiDocument || apiDocument.url.indexOf(searchParamId) < 0)
      ) {
        setIsFetching(true)

        const documentUrl = `${getLocalPath()}/halapi/documents/${searchParamId}`

        setApiDocument({ url: documentUrl })
        setApiItems(null)
      }
    }
  }, [page, component, parameters])

  useEffect(() => {
    if (apiDocument && apiDocument.url && !apiDocument.payload) {
      const fetchAsync = async () => {
        const data = await fetchData(apiDocument.url)
        if (data) {
          setApiDocument({ url: apiDocument.url, payload: data })
        }
      }

      fetchAsync()
    }
  }, [apiDocument])

  useEffect(() => {
    if (apiDocument && apiDocument.payload && !apiItems) {
      /*
        Example path: 'offerings/solutions/summary/equity/focused-large-cap-core'
        Example scope: '/offerings/solutions/detail/equity/xxx-large-cap-core/what'
        Example token: 'xxx-large-cap-core'
        */
      const pathSplit = apiDocument.payload['_meta']['path'].split('summary')

      const scope = parameters.token
        ? parameters.scope.replaceAll(
            parameters.token,
            pathSplit[pathSplit.length - 1]
          )
        : parameters.scope

      const itemsUrl = `${getLocalPath()}/halapi/documents?_scope=/content/documents/${getSharedSiteName()}${scope}&_sort=${
        parameters.sortField || 'cti:title'
      }&_limit=${parameters.pageSize}`

      setApiItems({ url: itemsUrl })
    }
  }, [apiDocument])

  useEffect(() => {
    if (apiItems && apiItems.url && !apiItems.payload) {
      const fetchAsync = async () => {
        const data = await fetchData(apiItems.url)
        if (data) {
          setApiItems({ url: apiItems.url, payload: data })
        }
      }

      fetchAsync()
    }
  }, [apiItems])

  useEffect(() => {
    if (apiItems && apiItems.payload) {
      const totalSize = apiItems.payload['_meta']?.['totalSize']
      if (totalSize > 0) {
        const items = unpackDocuments(
          apiItems.payload['_embedded']?.['documents']
        )
        setItems(items)
      } else {
        setItems([])
      }
      setIsFetching(false)
    }
  }, [apiItems])

  useEffect(() => {
    let didUpdate = false

    const fetchAsync = async (items) => {
      for (const item of items) {
        if (item.imageUrl && item.imageId && !item.imageDescription) {
          const imageData = await fetchData(
            `${getLocalPath()}/halapi/documents/${
              item.imageId
            }?_fields=description`
          )

          if (imageData?.description) {
            item.imageDescription = imageData?.description

            didUpdate = true
          }
        }
      }

      if (didUpdate) {
        setItems(items)
      }
    }

    fetchAsync([...items])
  }, [items])

  useEffect(() => {
    setSelectedItem((items && items.length > 0 && items[0]) || null)

    const _keys =
      items?.reduce(
        (prev, curr) => Array.from(new Set(prev.concat(curr.keys || []))),
        []
      ) || []

    setKeys(_keys.sort())

    const _filteredItems = items?.filter(
      (item) =>
        (selectedKeys.length === 0 ||
          item.keys?.some((item) => selectedKeys.includes(item))) &&
        (!selectedSearch ||
          item.title?.indexOf(selectedSearch) > -1 ||
          item.subtitle?.indexOf(selectedSearch) > -1 ||
          item.html?.indexOf(selectedSearch) > -1 ||
          (item.keys?.some((tags) =>
            tags.startsWith(
              selectedSearch.toLowerCase().replace(/[ &"']/g, '-')
            )
          ) &&
            selectedSearch.length > 0))
    )

    setFilteredItems(_filteredItems)

    const _columnCount = parameters.derived?.isCard
      ? isScreenMedium()
        ? 2
        : 3
      : _filteredItems?.length < 10 &&
          (_filteredItems?.length < 3 || _filteredItems?.length % 3 === 0)
        ? 3
        : 4
    const galleryColumnCount =
      parameters.derived?.isGallery && parameters.gridSize !== 0
        ? parameters.gridSize
        : _columnCount
    setColumnCount(galleryColumnCount)

    if (
      parameters.derived.isLinks ||
      parameters.derived.isAccordion ||
      parameters.derived.isTabs
    ) {
      const groupKey = parameters.derived.isLinks ? 'subtitle' : 'title'

      const itemGroups = _filteredItems.reduce((previous, current, index) => {
        const currentGroupKey = current[groupKey] || ''

        const existingGroup = previous.find(
          (group) => group.name === currentGroupKey
        )

        if (existingGroup) {
          existingGroup.items.push(current)
        } else {
          previous.push({
            name: currentGroupKey || '',
            expanded: parameters.derived.isTabs
              ? index === 0
                ? true
                : false
              : parameters.derived.isAccordion && currentGroupKey
                ? false
                : true,
            items: [current],
          })
        }

        if (parameters.derived.isLinks && currentGroupKey) {
          current.subtitle = ''
          current.html = ''
          current.imageUrl = ''
        }

        if (
          (parameters.derived.isAccordion && !parameters.derived.isLinks) ||
          parameters.derived.isTabs
        ) {
          current.title = ''
        }

        return previous
      }, [])

      setGroups(itemGroups)
    } else {
      if (parameters.derived.isGallery) {
        const longestTitle = _filteredItems.reduce((prev, curr) => {
          return Math.max(curr?.title?.length || 0, prev)
        }, 0)

        parameters.derived.isShortTitle = longestTitle < 7

        setParameters({ ...parameters })
      }

      setGroups([
        {
          name: '',
          expanded: true,
          items: _filteredItems,
        },
      ])
    }
  }, [items, selectedKeys, selectedSearch])

  useLayoutEffect(() => {
    window.addEventListener('scroll', onScroll)

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  })

  const onScroll = () => {
    const _isSticky = window.scrollY > 450

    setIsSticky(_isSticky)
  }

  const onClickKey = (key) => {
    const _selectedKeys = [...selectedKeys]

    updateArray(_selectedKeys, key, _selectedKeys.indexOf(key) < 0)

    setSelectedKeys(_selectedKeys)

    const filters = []
    _selectedKeys.forEach((key) => {
      filters.push(
        taxonomies.find((taxonomy) => taxonomy.key === key)?.name ||
          key.replaceAll(' ', ' ')
      )
    })

    window._satellite.track('getSelectedFilters', filters.join())
  }

  const onClickTitle = (item, index) => {
    setSelectedItem(item)

    const elementId = getGroupItemContainerId(0, index)

    setTimeout(() => {
      const _scrollTo =
        document.getElementById(elementId).offsetTop + (isSticky ? 382 : 300)

      window.scroll({
        top: _scrollTo,
        left: 0,
        behavior: 'smooth',
      })
    }, 100)
  }

  const onClickGroupExpand = (group) => {
    const groupExpanded = group.expanded

    if (parameters.derived.isTabs) {
      if (groupExpanded) {
        return
      }

      groups.forEach((g) => (g.expanded = false))
    }

    group.expanded = !groupExpanded

    setGroups([...groups])
  }

  const handleKeyDown = (group, e) => {
    if (e.key === 'Enter') {
      const groupExpanded = group.expanded

      if (parameters.derived.isTabs) {
        if (groupExpanded) {
          return
        }

        groups.forEach((g) => (g.expanded = false))
      }

      group.expanded = !groupExpanded

      setGroups([...groups])
    }
  }

  const onSearch = (event) => {
    const _value = event.currentTarget.value

    setSelectedSearch(_value)
  }

  const resetOnPageLoad = () => {
    if (parameters.filter === 'Show Terms' && searchTopic) {
      setSelectedKeys([searchTopic])

      setTimeout(() => {
        const _scrollTo = 500

        window.scroll({
          top: _scrollTo,
          left: 0,
          behavior: 'smooth',
        })
      }, 500)
    } else {
      setSelectedKeys([])
    }

    setSelectedSearch('')
  }

  const onClickClear = () => {
    setSelectedKeys([])
    setSelectedSearch('')
  }

  const getGroupItemContainerId = (groupIndex, itemIndex) => {
    return `id_group_item_container_${component.getId()}_group_${groupIndex}_item_${itemIndex}`
  }

  let stepIndex = 0

  const renderGroup = (group, groupIndex) => (
    <div
      id={`id_group_container_${component.getId()}_group_${groupIndex}`}
      key={groupIndex}
      className={removeSpaces(
        `\
          ${
            parameters.derived.isGallery
              ? parameters.gridSize !== 0
                ? 'Grid Grid--withGutter'
                : 'u-flex u-flexWrap' +
                  (filteredItems?.length === columnCount
                    ? ' u-flexJustifyBetween'
                    : '') +
                  (parameters.alignment === 'CardCenter'
                    ? ' u-lg-flexJustifyCenter'
                    : '') +
                  (parameters.alignment !== 'Center' ? ' u-sm-flexCol' : '')
              : ''
          } \
          ${
            parameters.derived.isAccordion && group.name
              ? 'u-decoBorderLight'
              : ''
          } \
          ${
            parameters.derived.isAccordion && group.name && groupIndex > 0
              ? 'u-decoBorderResetTop'
              : ''
          } \
          ${
            parameters.derived.isLinks && !parameters.derived.isAccordion
              ? 'u-marginVert6gu u-inlineBlock u-sizeFull'
              : ''
          } \
          ${
            parameters.derived.isTabs ? 'u-flex u-flexCol u-flexJustifyEnd' : ''
          } \
          `
      )}
    >
      {!parameters.derived.isTabs && group.name && (
        <div
          tabIndex={0}
          id={`id_group_header_${component.getId()}_group_${groupIndex}`}
          className={removeSpaces(
            `\
                u-flex \
                u-flexAlignItemsCenter \
                u-paddingVert2gu \
                ${
                  parameters.derived.isLinks && !parameters.derived.isAccordion
                    ? 'u-paddingBottom2gu'
                    : ''
                } \
                ${group.expanded ? 'open' : ''} \
                Accordion-toggle \
                `
          )}
          style={{
            cursor: parameters.derived.isAccordion ? 'pointer' : null,
          }}
          onClick={
            parameters.derived.isAccordion
              ? () => onClickGroupExpand(group)
              : null
          }
          onKeyDown={
            parameters.derived.isAccordion
              ? (e) => handleKeyDown(group, e)
              : null
          }
          data-analytics={
            parameters.derived.isAccordion && group?.name
              ? group?.name
                  .replaceAll('.', '')
                  .replaceAll(' ', '-')
                  .toLowerCase() + '-accordian'
              : undefined
          }
        >
          {parameters.derived.isAccordion && (
            <div className="u-textSizeMsrp2 u-marginRight2gu u-paddingBottom1gu">
              {group.expanded ? <div>&ndash;</div> : '+'}
            </div>
          )}
          <Title text={group.name} mode={'small'} textSize="u-textSizeMsrp1" />
        </div>
      )}
      {group.expanded &&
        group.items &&
        group.items.map((item, itemIndex) => {
          item.groupName = group?.name
          if (item.title) {
            stepIndex++
          }

          return (
            (parameters.filter !== 'Show Title' || item.html) && (
              <div
                key={itemIndex}
                id={getGroupItemContainerId(groupIndex, itemIndex)}
                className={removeSpaces(
                  `\
                      u-flex \
                      u-flexCol \
                      ${styles.itemBackgroundColor || ''} \
                      ${
                        parameters.derived.isSteps &&
                        itemIndex > 0 &&
                        item.title
                          ? 'u-decoBorderLight u-decoBorderResetBottom'
                          : parameters.derived.isCard
                            ? 'u-decoBorderLightFull'
                            : parameters.derived.isLinks &&
                                (!parameters.derived.isAccordion || !group.name)
                              ? (parameters.derived.isAccordion &&
                                  (!item.link || tryParseJSON(item.title))) ||
                                itemIndex > 0
                                ? 'u-decoBorderLight u-decoBorderResetTop'
                                : 'u-decoBorderLight'
                              : ''
                      } \
                      ${
                        parameters.derived.isAccordion && group.name
                          ? 'u-marginLeft4gu'
                          : ''
                      } \
                      ${
                        parameters.derived.isGallery &&
                        filteredItems?.length !== columnCount &&
                        (itemIndex + 1) % columnCount > 0
                          ? 'u-marginRight6gu u-md-marginRight3gu u-sm-marginRight0'
                          : ''
                      } \
                      ${
                        parameters.derived.isLinks
                          ? parameters.derived.isAccordion
                            ? itemIndex > 0 && !item.link && item.html
                              ? 'u-marginTop5gu u-paddingVert3gu'
                              : tryParseJSON(item.title)
                                ? 'u-paddingVert1gu'
                                : !group.name
                                  ? 'u-paddingVert3gu'
                                  : 'u-paddingVert2gu'
                            : 'u-paddingVert2gu u-textSizeMsr'
                          : parameters.derived.isGallery ||
                              parameters.derived.isSteps
                            ? 'u-marginBottom5gu'
                            : !(
                                  parameters.derived.isBanner ||
                                  (item?.videoId &&
                                    item?.itemType === 'Video list')
                                )
                              ? 'u-marginBottom6gu'
                              : ''
                      } \
                      ${
                        parameters.derived.isGallery
                          ? 'u-sm-sizeFull u-md-size1of' +
                            columnCount +
                            ' u-lg-size1of' +
                            columnCount
                          : ''
                      } \
                      `
                )}
                data-analytics={
                  parameters.derived.isCard ? 'insights-card' : undefined
                }
              >
                {parameters.derived.isSteps && item.title && (
                  <div
                    className={`u-flex u-flexJustifyCenter ${
                      stepIndex > 1 ? 'u-marginTop5gu' : ''
                    }`}
                  >
                    <div
                      className="u-bgColorDark u-textColorWhite u-paddingVert1gu u-paddingHoriz2gu u-textSizeMsrp2"
                      style={{ borderRadius: '50%' }}
                    >
                      {stepIndex}
                    </div>
                  </div>
                )}
                {parameters.derived.isBanner ||
                (item?.videoId && item?.itemType === 'Video list') ? (
                  <Banner
                    banner={'video'}
                    page={page}
                    component={null}
                    item={item}
                    params={{
                      ...parameters,
                      style:
                        item?.videoId && item?.itemType === 'Video list'
                          ? 'VideoBlog'
                          : parameters.style,
                      backgroundColor: null,
                      position: 'Left',
                    }}
                  />
                ) : (
                  <ArticleDetail
                    page={page}
                    component={null}
                    item={item}
                    params={{
                      ...parameters,
                      style: parameters.derived.isSteps
                        ? 'Callout'
                        : parameters.style,
                    }}
                  />
                )}
              </div>
            )
          )
        })}
      {parameters.filter === 'Show Terms' && filteredItems?.length < 1 && (
        <div className="u-flex u-flexCol u-flexAlignItemsCenter u-sizeFull u-marginVert12gu">
          <Title
            text="No results found"
            textSize="u-textSizeMsrp2"
            mode="small"
          />
          <div className="u-marginTop5gu">
            <span className="Link" onClick={onClickClear}>
              Clear all filters
            </span>
            &nbsp;or try another search.
          </div>
        </div>
      )}
    </div>
  )

  return (
    !isFetching &&
    items?.length > 0 && (
      <div
        id={blurb.title?.replaceAll(' ', '').replaceAll("'", '')}
        className={`ArticleList2 ${
          navigation.navigationItems.find((el) => el.id === component.getId())
            ? 'u-ls-marginLeftSmall'
            : ''
        } ${
          !parameters.insetBackground && styles.backgroundColor
            ? styles.backgroundColor
            : ''
        } ${
          !parameters.insetBackground && !styles.backgroundColor
            ? getMargins(parameters.marginTop, parameters.marginBottom)
            : ''
        }`}
      >
        <div
          className={`u-posRelative u-sizeConstrained ${
            parameters.derived.isNarrow ||
            (parameters.derived.isTabs && !parameters.derived.isTabsWide)
              ? 'u-sizeConstrainedNarrow'
              : ''
          } ${
            pageLayout.container === 'main' ||
            ['1', '2', '3', '4', '5', '6', '7', '8'].includes(
              pageLayout.container
            )
              ? parameters.insetBackground || styles.backgroundColor
                ? `u-paddingTop12gu u-paddingBottom8gu ${parameters.insetBackground && styles.backgroundColor ? styles.backgroundColor : ''}`
                : ''
              : ''
          } ${
            parameters.derived.isLinks && parameters.derived.isAccordion
              ? 'u-flex u-sm-flexCol'
              : ''
          }`}
        >
          {blurb.document && <BrManageContentButton content={blurb.document} />}
          {
            <div
              className={`${
                blurb.title
                  ? parameters.derived.isGallery ||
                    parameters.derived.isAccordion ||
                    parameters.derived.isTabs ||
                    parameters.style === 'Callout'
                    ? 'u-marginBottom8gu'
                    : 'u-marginBottom2gu'
                  : ''
              } ${
                parameters.derived.isAccordion && parameters.derived.isLinks
                  ? 'u-size1of2 u-sm-sizeFull u-paddingRight5gu'
                  : ''
              }`}
            >
              <ArticleDetail
                page={page}
                component={component}
                params={{
                  ...parameters,
                  alignment: parameters.derived.isSteps ? 'Center' : null,
                  style: null,
                  derived: {
                    isGallery: parameters.derived.isGallery,
                    isList: parameters.derived.isList,
                    isLinks: parameters.derived.isLinks,
                    isAccordion: parameters.derived.isAccordion,
                  },
                }}
              />
            </div>
          }
          {parameters.filter === 'Show Title' && (
            <div
              className={`${
                isSticky ? 'u-posFixed u-boxShadowBottom u-paddingTop5gu' : ''
              } u-sizeFull u-marginBottom10gu u-decoBorderLight u-decoBorderResetTop u-bgColorWhite`}
              style={{
                top: isSticky ? navContainerHeight : null,
                left: 0,
                zIndex: 1,
                userSelect: 'none',
              }}
            >
              <div className={`${isSticky ? 'u-sizeConstrained' : ''} u-flex`}>
                {items?.map((item, index) => (
                  <div key={index} className="u-marginRight3gu">
                    {item.html ? (
                      <button
                        className={`u-textSecondary u-textCenter hoverable focusable u-textBold u-paddingHoriz2gu u-paddingVert1gu`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => onClickTitle(item, index)}
                      >
                        {item.title}
                      </button>
                    ) : (
                      <div
                        className={`u-textSecondary u-textCenter u-textColorLightest u-paddingHoriz2gu u-paddingVert1gu`}
                      >
                        {item.title}
                      </div>
                    )}
                    <div
                      className={`${
                        selectedItem?.title === item.title
                          ? 'u-bgColorBrandLight'
                          : ''
                      }`}
                      style={{ width: '100%', height: 3 }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          {parameters.filter === 'Show Terms' && (
            <>
              {items?.length > 9 && (
                <div
                  className="SearchFilter-width u-posAbsolute"
                  style={{
                    top: isScreenSmall() ? 50 : -3,
                    left: isScreenSmall() ? 15 : null,
                    right: isScreenSmall() ? null : '1.25rem',
                  }}
                >
                  <Filter
                    placeholder="Search insights"
                    defaultSearchText={selectedSearch}
                    mode={'on-key-press'}
                    onSearch={onSearch}
                    inputDataAnalytics="search-insights"
                  />
                </div>
              )}
              <div className="u-marginTop3gu u-sm-marginTop15gu u-marginBottom5gu">
                <Title text="Filter by:" mode="medium-large" />
                <div
                  className="Chip-group u-marginTop3gu"
                  style={{ userSelect: 'none' }}
                >
                  {
                    // TODO: Hardcode of 'client-materials'
                    keys
                      ?.filter((k) => k !== 'client-materials')
                      .map((key) => (
                        <div
                          key={key}
                          tabIndex="0"
                          className={`Chip ${
                            selectedKeys.indexOf(key) > -1
                              ? 'Chip--color active'
                              : ''
                          } u-textCapitalize`}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => onClickKey(key)}
                          data-analytics={
                            isInstitutional
                              ? 'insights-filters'
                              : selectedKeys.indexOf(key) === -1
                                ? 'practice-management-filter-chip-inactive'
                                : undefined
                          }
                        >
                          {(taxonomies?.length > 0 &&
                            taxonomies.find((taxonomy) => taxonomy.key === key)
                              ?.name) ||
                            key.replaceAll(' ', ' ')}
                        </div>
                      ))
                  }
                </div>
              </div>
              <div className="u-flex u-flexAlignItemsCenter u-flexJustifyBetween u-marginBottom5gu">
                <div className="u-textSecondary">
                  Displaying{' '}
                  <span className="u-textBold">
                    {filteredItems?.length || 0}
                  </span>{' '}
                  of <span className="u-textBold">{items?.length || 0}</span>{' '}
                  insights
                </div>
                <div
                  className={`Link u-textSecondary ${
                    selectedKeys.length === 0 && !selectedSearch
                      ? 'u-disabled'
                      : ''
                  }`}
                  onClick={onClickClear}
                >
                  Clear all filters
                </div>
              </div>
            </>
          )}
          <div
            id={`id_groups_container_${component.getId()}`}
            className={removeSpaces(
              `\
              ${
                parameters.derived.isAccordion
                  ? 'u-size1of2 u-sm-sizeFull u-paddingBottom2gu'
                  : ''
              } \
              ${
                parameters.derived.isLinks &&
                !parameters.derived.isAccordion &&
                parameters.gridSize === 0
                  ? 'gridwrapper'
                  : ''
              } \
              ${parameters.derived.isTabs ? 'u-flex u-flexCol' : ''} \
              `
            )}
            data-analytics={
              parameters.derived.isAccordion && blurb?.title
                ? `${blurb?.title
                    ?.replaceAll('.', '')
                    .replaceAll(' ', '-')
                    .toLowerCase()}-accordian`
                : undefined
            }
          >
            {parameters.derived.isTabs && (
              <div
                id={`id_groups_tabs_${component.getId()}`}
                className="u-flex u-sm-flexCol u-decoBorderLight u-decoBorderResetTop u-textSizeMsrm1"
              >
                {groups.map((group, groupIndex) => (
                  <div
                    id={`id_group_header_tab_${component.getId()}-group-${groupIndex}`}
                    key={groupIndex}
                    className={`u-flex u-flexCol u-flexJustifyEnd ${
                      groupIndex > 0 ? 'u-paddingHoriz2gu' : 'u-paddingRight2gu'
                    } u-sm-paddingHoriz0`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onClickGroupExpand(group)}
                  >
                    <div
                      className={`u-paddingVert2gu u-sm-paddingHoriz2gu ${
                        group.expanded
                          ? 'u-sm-bgColorBrandExtraLight u-sm-txt-white'
                          : ''
                      }`}
                    >
                      <Title
                        text={group.name}
                        mode={'small'}
                        textColor="u-textColorBlack"
                      />
                    </div>
                    <div
                      className={`${
                        group.expanded ? 'u-bgColorBrandLight' : ''
                      } u-sm-hidden`}
                      style={{ width: '100%', height: 2 }}
                    />
                  </div>
                ))}
              </div>
            )}
            {parameters.derived.isLinks && parameters.gridSize !== 0 ? (
              <Grid className="u-marginVert6gu">
                {groups.map((group) => {
                  return group.items.map((el, index) => {
                    return (
                      <GridItem
                        key={el.id}
                        gridSize={parameters.gridSize}
                        className="u-md-size1of2 u-sm-sizeFull"
                      >
                        <div
                          className={`u-decoBorderLight  ${
                            index >
                            parameters.derived.columnCountBasedOnScreenSize - 1
                              ? 'u-decoBorderResetTop'
                              : ''
                          } u-paddingVert4gu u-marginRight4gu`}
                        >
                          <ArticleDetail
                            page={page}
                            component={null}
                            item={el}
                            params={{
                              ...parameters,
                              style: parameters.derived.isSteps
                                ? 'Callout'
                                : parameters.style,
                            }}
                          />

                          {/* <a
                            className="u-textKernLoose u-textColorBrandLight"
                            href=""
                          >
                            {el.title}
                          </a> */}
                        </div>
                      </GridItem>
                    )
                  })
                })}
              </Grid>
            ) : (
              groups.map((group, groupIndex) =>
                parameters.derived.isLinks &&
                !parameters.derived.isAccordion ? (
                  <div key={groupIndex}>{renderGroup(group, groupIndex)}</div>
                ) : (
                  renderGroup(group, groupIndex)
                )
              )
            )}
          </div>
          {parameters.derived.isGallery && (
            <div className="u-flex">
              {blurb.link?.text && (
                <Link2
                  url={getLinkUrl(blurb.link)}
                  text={blurb.link.text}
                  arrow="brand"
                  className={`Link u-posRelative hover-underline-animation`}
                  textClasses={`u-textSecondary u-textKernLoose`}
                />
              )}
            </div>
          )}
        </div>
      </div>
    )
  )
}

ArticleList.propTypes = {
  ...types,
}
